/**
 * Threat Classification Pipeline
 * ================================
 * 3-stage keyword-based threat classifier for the DetectX Intel MVP.
 *
 * Stage 1: Tokenize input and normalize to lowercase.
 * Stage 2: Match tokens against 120+ keyword rules using word-boundary regex.
 * Stage 3: Aggregate matches — return highest severity found, primary category,
 *          confidence score, and all matched keywords.
 *
 * Confidence heuristic:
 *   base = severity weight (critical=0.95, high=0.8, medium=0.6, low=0.4, info=0.2)
 *   boost = min(0.05, matchedKeywords.length * 0.02) — more matches = higher confidence
 *   final = min(1.0, base + boost)
 */

import type { ThreatLevel, EventType } from '@/types';

// ---------------------------------------------------------------------------
// Keyword → (severity, category) mapping
// ---------------------------------------------------------------------------

interface KeywordRule {
  keyword: string;
  severity: ThreatLevel;
  category: EventType;
}

/**
 * Master keyword table. Each entry maps a keyword (matched with word boundaries)
 * to a severity level and an event category. Organized by category for readability.
 */
const KEYWORD_RULES: KeywordRule[] = [
  // ── Conflict (critical + high) ──────────────────────────────────────────
  { keyword: 'killed',       severity: 'critical', category: 'conflict' },
  { keyword: 'massacre',     severity: 'critical', category: 'conflict' },
  { keyword: 'invasion',     severity: 'critical', category: 'conflict' },
  { keyword: 'genocide',     severity: 'critical', category: 'conflict' },
  { keyword: 'war crime',    severity: 'critical', category: 'conflict' },
  { keyword: 'ethnic cleansing', severity: 'critical', category: 'conflict' },
  { keyword: 'casualties',   severity: 'critical', category: 'conflict' },
  { keyword: 'death toll',   severity: 'critical', category: 'conflict' },
  { keyword: 'mass graves',  severity: 'critical', category: 'conflict' },
  { keyword: 'attack',       severity: 'high',     category: 'conflict' },
  { keyword: 'airstrike',    severity: 'high',     category: 'conflict' },
  { keyword: 'shelling',     severity: 'high',     category: 'conflict' },
  { keyword: 'offensive',    severity: 'high',     category: 'conflict' },
  { keyword: 'clashes',      severity: 'high',     category: 'conflict' },
  { keyword: 'fighting',     severity: 'high',     category: 'conflict' },
  { keyword: 'combat',       severity: 'high',     category: 'conflict' },
  { keyword: 'ceasefire violation', severity: 'high', category: 'conflict' },
  { keyword: 'skirmish',     severity: 'medium',   category: 'conflict' },
  { keyword: 'tension',      severity: 'medium',   category: 'conflict' },
  { keyword: 'standoff',     severity: 'medium',   category: 'conflict' },
  { keyword: 'ceasefire',    severity: 'low',      category: 'conflict' },
  { keyword: 'peace talks',  severity: 'low',      category: 'conflict' },
  { keyword: 'truce',        severity: 'low',      category: 'conflict' },

  // ── Terrorism ───────────────────────────────────────────────────────────
  { keyword: 'bombing',      severity: 'high',     category: 'terrorism' },
  { keyword: 'suicide bomb',  severity: 'critical', category: 'terrorism' },
  { keyword: 'car bomb',     severity: 'critical', category: 'terrorism' },
  { keyword: 'terrorist',    severity: 'high',     category: 'terrorism' },
  { keyword: 'terrorism',    severity: 'high',     category: 'terrorism' },
  { keyword: 'extremist',    severity: 'high',     category: 'terrorism' },
  { keyword: 'militant',     severity: 'high',     category: 'terrorism' },
  { keyword: 'insurgent',    severity: 'high',     category: 'terrorism' },
  { keyword: 'hostage',      severity: 'high',     category: 'terrorism' },
  { keyword: 'kidnapping',   severity: 'high',     category: 'terrorism' },
  { keyword: 'abduction',    severity: 'high',     category: 'terrorism' },
  { keyword: 'beheading',    severity: 'critical', category: 'terrorism' },
  { keyword: 'ied',          severity: 'high',     category: 'terrorism' },
  { keyword: 'radicalization', severity: 'medium', category: 'terrorism' },

  // ── Military ────────────────────────────────────────────────────────────
  { keyword: 'nuclear',      severity: 'critical', category: 'military' },
  { keyword: 'nuclear test', severity: 'critical', category: 'military' },
  { keyword: 'ballistic missile', severity: 'critical', category: 'military' },
  { keyword: 'icbm',         severity: 'critical', category: 'military' },
  { keyword: 'missile',      severity: 'high',     category: 'military' },
  { keyword: 'missile launch', severity: 'critical', category: 'military' },
  { keyword: 'troops',       severity: 'high',     category: 'military' },
  { keyword: 'deployment',   severity: 'high',     category: 'military' },
  { keyword: 'mobilization', severity: 'high',     category: 'military' },
  { keyword: 'warship',      severity: 'high',     category: 'military' },
  { keyword: 'naval',        severity: 'medium',   category: 'military' },
  { keyword: 'submarine',    severity: 'high',     category: 'military' },
  { keyword: 'fighter jet',  severity: 'high',     category: 'military' },
  { keyword: 'drone strike', severity: 'high',     category: 'military' },
  { keyword: 'drone',        severity: 'medium',   category: 'military' },
  { keyword: 'military exercise', severity: 'medium', category: 'military' },
  { keyword: 'arms deal',    severity: 'medium',   category: 'military' },
  { keyword: 'ammunition',   severity: 'medium',   category: 'military' },
  { keyword: 'gps jamming',  severity: 'high',     category: 'military' },
  { keyword: 'conscription', severity: 'high',     category: 'military' },
  { keyword: 'defense pact', severity: 'medium',   category: 'military' },
  { keyword: 'no-fly zone',  severity: 'high',     category: 'military' },

  // ── Protest ─────────────────────────────────────────────────────────────
  { keyword: 'protest',      severity: 'medium',   category: 'protest' },
  { keyword: 'demonstration', severity: 'medium',  category: 'protest' },
  { keyword: 'rally',        severity: 'medium',   category: 'protest' },
  { keyword: 'riot',         severity: 'high',     category: 'protest' },
  { keyword: 'uprising',     severity: 'high',     category: 'protest' },
  { keyword: 'unrest',       severity: 'high',     category: 'protest' },
  { keyword: 'tear gas',     severity: 'high',     category: 'protest' },
  { keyword: 'crackdown',    severity: 'high',     category: 'protest' },
  { keyword: 'martial law',  severity: 'critical', category: 'protest' },
  { keyword: 'curfew',       severity: 'high',     category: 'protest' },
  { keyword: 'strike',       severity: 'medium',   category: 'protest' },
  { keyword: 'blockade',     severity: 'medium',   category: 'protest' },

  // ── Diplomatic ──────────────────────────────────────────────────────────
  { keyword: 'sanctions',    severity: 'medium',   category: 'diplomatic' },
  { keyword: 'embargo',      severity: 'medium',   category: 'diplomatic' },
  { keyword: 'expelled',     severity: 'high',     category: 'diplomatic' },
  { keyword: 'ambassador recalled', severity: 'high', category: 'diplomatic' },
  { keyword: 'diplomatic crisis', severity: 'high', category: 'diplomatic' },
  { keyword: 'summit',       severity: 'low',      category: 'diplomatic' },
  { keyword: 'treaty',       severity: 'low',      category: 'diplomatic' },
  { keyword: 'agreement',    severity: 'low',      category: 'diplomatic' },
  { keyword: 'talks',        severity: 'low',      category: 'diplomatic' },
  { keyword: 'negotiations', severity: 'low',      category: 'diplomatic' },
  { keyword: 'alliance',     severity: 'low',      category: 'diplomatic' },
  { keyword: 'diplomatic row', severity: 'medium', category: 'diplomatic' },
  { keyword: 'trade war',    severity: 'high',     category: 'diplomatic' },
  { keyword: 'tariff',       severity: 'medium',   category: 'diplomatic' },
  { keyword: 'retaliation',  severity: 'high',     category: 'diplomatic' },
  { keyword: 'annexation',   severity: 'critical', category: 'diplomatic' },

  // ── Economic ────────────────────────────────────────────────────────────
  { keyword: 'recession',    severity: 'high',     category: 'economic' },
  { keyword: 'default',      severity: 'critical', category: 'economic' },
  { keyword: 'debt crisis',  severity: 'critical', category: 'economic' },
  { keyword: 'inflation',    severity: 'medium',   category: 'economic' },
  { keyword: 'currency collapse', severity: 'critical', category: 'economic' },
  { keyword: 'stock crash',  severity: 'high',     category: 'economic' },
  { keyword: 'market crash', severity: 'high',     category: 'economic' },
  { keyword: 'bank run',     severity: 'critical', category: 'economic' },
  { keyword: 'trade',        severity: 'low',      category: 'economic' },
  { keyword: 'gdp',          severity: 'low',      category: 'economic' },
  { keyword: 'unemployment', severity: 'medium',   category: 'economic' },
  { keyword: 'supply chain', severity: 'medium',   category: 'economic' },

  // ── Disaster ────────────────────────────────────────────────────────────
  { keyword: 'earthquake',   severity: 'high',     category: 'disaster' },
  { keyword: 'tsunami',      severity: 'critical', category: 'disaster' },
  { keyword: 'hurricane',    severity: 'high',     category: 'disaster' },
  { keyword: 'typhoon',      severity: 'high',     category: 'disaster' },
  { keyword: 'cyclone',      severity: 'high',     category: 'disaster' },
  { keyword: 'tornado',      severity: 'high',     category: 'disaster' },
  { keyword: 'flood',        severity: 'high',     category: 'disaster' },
  { keyword: 'flooding',     severity: 'high',     category: 'disaster' },
  { keyword: 'landslide',    severity: 'high',     category: 'disaster' },
  { keyword: 'volcano',      severity: 'high',     category: 'disaster' },
  { keyword: 'eruption',     severity: 'high',     category: 'disaster' },
  { keyword: 'wildfire',     severity: 'high',     category: 'disaster' },
  { keyword: 'famine',       severity: 'critical', category: 'disaster' },
  { keyword: 'drought',      severity: 'medium',   category: 'disaster' },

  // ── Cyber ───────────────────────────────────────────────────────────────
  { keyword: 'cyberattack',  severity: 'high',     category: 'cyber' },
  { keyword: 'cyber attack', severity: 'high',     category: 'cyber' },
  { keyword: 'ransomware',   severity: 'high',     category: 'cyber' },
  { keyword: 'data breach',  severity: 'high',     category: 'cyber' },
  { keyword: 'hacked',       severity: 'high',     category: 'cyber' },
  { keyword: 'hack',         severity: 'high',     category: 'cyber' },
  { keyword: 'malware',      severity: 'high',     category: 'cyber' },
  { keyword: 'phishing',     severity: 'medium',   category: 'cyber' },
  { keyword: 'ddos',         severity: 'medium',   category: 'cyber' },
  { keyword: 'zero-day',     severity: 'critical', category: 'cyber' },
  { keyword: 'exploit',      severity: 'medium',   category: 'cyber' },
  { keyword: 'vulnerability', severity: 'medium',  category: 'cyber' },

  // ── Health ──────────────────────────────────────────────────────────────
  { keyword: 'pandemic',     severity: 'critical', category: 'health' },
  { keyword: 'epidemic',     severity: 'high',     category: 'health' },
  { keyword: 'outbreak',     severity: 'high',     category: 'health' },
  { keyword: 'quarantine',   severity: 'high',     category: 'health' },
  { keyword: 'lockdown',     severity: 'high',     category: 'health' },
  { keyword: 'vaccine',      severity: 'low',      category: 'health' },
  { keyword: 'virus',        severity: 'medium',   category: 'health' },
  { keyword: 'infection',    severity: 'medium',   category: 'health' },
  { keyword: 'contamination', severity: 'high',    category: 'health' },
  { keyword: 'bioweapon',    severity: 'critical', category: 'health' },

  // ── Environmental ───────────────────────────────────────────────────────
  { keyword: 'oil spill',    severity: 'high',     category: 'environmental' },
  { keyword: 'deforestation', severity: 'medium',  category: 'environmental' },
  { keyword: 'pollution',    severity: 'medium',   category: 'environmental' },
  { keyword: 'toxic',        severity: 'high',     category: 'environmental' },
  { keyword: 'radiation',    severity: 'critical', category: 'environmental' },
  { keyword: 'meltdown',     severity: 'critical', category: 'environmental' },
  { keyword: 'chemical spill', severity: 'high',   category: 'environmental' },
  { keyword: 'emissions',    severity: 'low',      category: 'environmental' },
  { keyword: 'climate',      severity: 'low',      category: 'environmental' },

  // ── Infrastructure ─────────────────────────────────────────────────────
  { keyword: 'pipeline explosion', severity: 'critical', category: 'infrastructure' },
  { keyword: 'power outage', severity: 'high',     category: 'infrastructure' },
  { keyword: 'blackout',     severity: 'high',     category: 'infrastructure' },
  { keyword: 'grid failure', severity: 'critical', category: 'infrastructure' },
  { keyword: 'bridge collapse', severity: 'critical', category: 'infrastructure' },
  { keyword: 'dam failure',  severity: 'critical', category: 'infrastructure' },
  { keyword: 'derailment',   severity: 'high',     category: 'infrastructure' },
  { keyword: 'explosion',    severity: 'high',     category: 'infrastructure' },
  { keyword: 'sabotage',     severity: 'high',     category: 'infrastructure' },

  // ── Crime ───────────────────────────────────────────────────────────────
  { keyword: 'assassination', severity: 'critical', category: 'crime' },
  { keyword: 'coup',         severity: 'critical', category: 'crime' },
  { keyword: 'coup attempt', severity: 'critical', category: 'crime' },
  { keyword: 'shooting',     severity: 'high',     category: 'crime' },
  { keyword: 'mass shooting', severity: 'critical', category: 'crime' },
  { keyword: 'cartel',       severity: 'high',     category: 'crime' },
  { keyword: 'trafficking',  severity: 'high',     category: 'crime' },
  { keyword: 'smuggling',    severity: 'medium',   category: 'crime' },
  { keyword: 'organized crime', severity: 'high',  category: 'crime' },

  // ── Tech ────────────────────────────────────────────────────────────────
  { keyword: 'ai regulation', severity: 'low',     category: 'tech' },
  { keyword: 'chip ban',     severity: 'medium',   category: 'tech' },
  { keyword: 'semiconductor', severity: 'low',     category: 'tech' },
  { keyword: 'tech ban',     severity: 'medium',   category: 'tech' },
  { keyword: 'surveillance', severity: 'medium',   category: 'tech' },
  { keyword: 'censorship',   severity: 'medium',   category: 'tech' },
  { keyword: 'disinformation', severity: 'medium', category: 'tech' },
  { keyword: 'deepfake',     severity: 'medium',   category: 'tech' },
];

// ---------------------------------------------------------------------------
// Precomputed lookup structures
// ---------------------------------------------------------------------------

/** Severity ordering — higher index = more severe. */
const SEVERITY_RANK: Record<ThreatLevel, number> = {
  info: 0,
  low: 1,
  medium: 2,
  high: 3,
  critical: 4,
};

/** Base confidence assigned to each severity level. */
const SEVERITY_CONFIDENCE: Record<ThreatLevel, number> = {
  info: 0.2,
  low: 0.4,
  medium: 0.6,
  high: 0.8,
  critical: 0.95,
};

/**
 * Precompiled regex cache. Each keyword is compiled once into a word-boundary
 * regex for efficient reuse across calls.
 */
const COMPILED_RULES: { regex: RegExp; rule: KeywordRule }[] = KEYWORD_RULES.map(
  (rule) => ({
    regex: new RegExp(`\\b${escapeRegex(rule.keyword)}\\b`, 'i'),
    rule,
  }),
);

/** Escape special regex characters in a string. */
function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// ---------------------------------------------------------------------------
// Classification result type
// ---------------------------------------------------------------------------

export interface ClassificationResult {
  /** Highest threat severity detected in the text. */
  level: ThreatLevel;
  /** Primary event category (from the highest-severity match). */
  category: EventType;
  /** Confidence score in [0, 1]. */
  confidence: number;
  /** All keyword strings that matched. */
  keywords: string[];
}

// ---------------------------------------------------------------------------
// Stage 1: Text normalization
// ---------------------------------------------------------------------------

/**
 * Normalize input text for matching. Lowercases and collapses whitespace
 * so that multi-word keywords can match across line breaks.
 */
function normalizeText(text: string): string {
  return text.toLowerCase().replace(/\s+/g, ' ').trim();
}

// ---------------------------------------------------------------------------
// Stage 2: Keyword matching
// ---------------------------------------------------------------------------

interface KeywordMatch {
  keyword: string;
  severity: ThreatLevel;
  category: EventType;
  rank: number;
}

/**
 * Scan normalized text against all compiled keyword rules.
 * Returns every match with its severity rank for downstream aggregation.
 */
function matchKeywords(normalizedText: string): KeywordMatch[] {
  const matches: KeywordMatch[] = [];

  for (const { regex, rule } of COMPILED_RULES) {
    if (regex.test(normalizedText)) {
      matches.push({
        keyword: rule.keyword,
        severity: rule.severity,
        category: rule.category,
        rank: SEVERITY_RANK[rule.severity],
      });
    }
  }

  return matches;
}

// ---------------------------------------------------------------------------
// Stage 3: Aggregation
// ---------------------------------------------------------------------------

/**
 * Aggregate keyword matches into a single classification result.
 *
 * - Level: highest severity found.
 * - Category: category of the highest-severity match. Ties broken by the
 *   category that appears most frequently among the top-severity matches.
 * - Confidence: base confidence for the severity level, boosted by match count.
 * - Keywords: deduplicated list of all matched keyword strings.
 */
function aggregateMatches(matches: KeywordMatch[]): ClassificationResult {
  if (matches.length === 0) {
    return { level: 'info', category: 'news', confidence: 0.2, keywords: [] };
  }

  // Determine highest severity
  let maxRank = -1;
  for (const m of matches) {
    if (m.rank > maxRank) maxRank = m.rank;
  }

  const topSeverity = matches.find((m) => m.rank === maxRank)!.severity;

  // Among matches at the highest severity, pick the most frequent category.
  // This ensures the primary category reflects the dominant theme.
  const topMatches = matches.filter((m) => m.rank === maxRank);
  const categoryCounts = new Map<EventType, number>();
  for (const m of topMatches) {
    categoryCounts.set(m.category, (categoryCounts.get(m.category) ?? 0) + 1);
  }
  let primaryCategory: EventType = topMatches[0].category;
  let maxCategoryCount = 0;
  for (const [cat, count] of categoryCounts) {
    if (count > maxCategoryCount) {
      maxCategoryCount = count;
      primaryCategory = cat;
    }
  }

  // Confidence: base from severity + small boost per additional match
  const base = SEVERITY_CONFIDENCE[topSeverity];
  const boost = Math.min(0.05, matches.length * 0.02);
  const confidence = Math.min(1.0, base + boost);

  // Deduplicated keyword list
  const keywords = [...new Set(matches.map((m) => m.keyword))];

  return {
    level: topSeverity,
    category: primaryCategory,
    confidence: Math.round(confidence * 1000) / 1000, // 3 decimal places
    keywords,
  };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Classify a single text string through the 3-stage pipeline.
 *
 * @param text - Raw text to classify (headline, description, or body).
 * @returns Classification result with threat level, category, confidence, and matched keywords.
 *
 * @example
 * ```ts
 * const result = classifyThreat('Missile launched toward northern border');
 * // { level: 'high', category: 'military', confidence: 0.82, keywords: ['missile'] }
 * ```
 */
export function classifyThreat(text: string): ClassificationResult {
  // Stage 1: Normalize
  const normalized = normalizeText(text);

  // Stage 2: Match
  const matches = matchKeywords(normalized);

  // Stage 3: Aggregate
  return aggregateMatches(matches);
}

/**
 * Classify a batch of items efficiently.
 *
 * Accepts an array of `{ id, text }` objects and returns a Map keyed by id.
 * This avoids redundant function-call overhead and keeps results associated
 * with their source items.
 *
 * @param items - Array of items to classify, each with an `id` and `text`.
 * @returns Map from item id to classification result.
 *
 * @example
 * ```ts
 * const results = classifyBatch([
 *   { id: 'evt-1', text: 'Massive earthquake hits coastal city' },
 *   { id: 'evt-2', text: 'Trade agreement signed between nations' },
 * ]);
 * results.get('evt-1'); // { level: 'high', category: 'disaster', ... }
 * ```
 */
export function classifyBatch(
  items: { id: string; text: string }[],
): Map<string, ClassificationResult> {
  const results = new Map<string, ClassificationResult>();

  for (const item of items) {
    results.set(item.id, classifyThreat(item.text));
  }

  return results;
}

/**
 * Utility: compare two threat levels.
 * Returns positive if `a` is more severe than `b`, 0 if equal, negative otherwise.
 */
export function compareSeverity(a: ThreatLevel, b: ThreatLevel): number {
  return SEVERITY_RANK[a] - SEVERITY_RANK[b];
}

/**
 * Utility: get the numeric rank of a threat level (0=info, 4=critical).
 */
export function severityRank(level: ThreatLevel): number {
  return SEVERITY_RANK[level];
}
